import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '../api/settings';
import { toErrorMessage } from '../api/client';
import { useStores } from '../auth/StoreContext';
import { Card, PageHead } from '../components/ui';
import { Empty, ErrorState, Loading } from '../components/DataStates';
import { StoreSelector } from '../components/StoreSelector';
import type { StoreSettings, StoreSettingsUpdate } from '../types';

export function SettingsPage() {
  const { selectedStoreId } = useStores();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<StoreSettings | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['settings', selectedStoreId],
    enabled: selectedStoreId != null,
    queryFn: () => settingsApi.get(selectedStoreId as number),
  });

  useEffect(() => {
    if (q.data) setDraft(q.data);
  }, [q.data]);

  const m = useMutation({
    mutationFn: (body: StoreSettingsUpdate) => settingsApi.update(selectedStoreId as number, body),
    onSuccess: (data) => {
      setDraft(data);
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['settings', selectedStoreId] });
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (e) => setError(toErrorMessage(e)),
  });

  const set = (patch: Partial<StoreSettings>) =>
    setDraft((d) => (d ? { ...d, ...patch } : d));

  function save() {
    if (!draft) return;
    setError(null);
    m.mutate({
      gstRate: Number(draft.gstRate),
      cgstRate: Number(draft.cgstRate),
      sgstRate: Number(draft.sgstRate),
      receiptFooter: draft.receiptFooter,
      logoUrl: draft.logoUrl,
      allowCashierDiscount: draft.allowCashierDiscount,
      maxDiscountPercent: Number(draft.maxDiscountPercent),
      requireAdminCancel: draft.requireAdminCancel,
    });
  }

  return (
    <>
      <PageHead title="Settings" subtitle="Tax, receipt and billing configuration per store." actions={<StoreSelector />} />

      {selectedStoreId == null ? (
        <Card><Empty icon="🏬" title="Select a store" /></Card>
      ) : q.isLoading || !draft ? (
        <Card><Loading /></Card>
      ) : q.isError ? (
        <Card><ErrorState message={toErrorMessage(q.error)} onRetry={q.refetch} /></Card>
      ) : (
        <>
          <Card title="Tax Configuration">
            <div className="callout callout-amber" style={{ marginBottom: 16 }}>
              ⚠️ Changing tax rates affects only <strong>future</strong> bills. Historical bills keep the tax
              rate that was in effect when they were generated.
            </div>
            <div className="field-row">
              <div className="field">
                <label>GST Rate (%)</label>
                <input type="number" step="0.01" value={draft.gstRate}
                  onChange={(e) => set({ gstRate: Number(e.target.value) })} />
              </div>
              <div className="field">
                <label>CGST Rate (%)</label>
                <input type="number" step="0.01" value={draft.cgstRate}
                  onChange={(e) => set({ cgstRate: Number(e.target.value) })} />
              </div>
              <div className="field">
                <label>SGST Rate (%)</label>
                <input type="number" step="0.01" value={draft.sgstRate}
                  onChange={(e) => set({ sgstRate: Number(e.target.value) })} />
              </div>
            </div>
          </Card>

          <Card title="Receipt & Billing">
            <div className="field">
              <label>Receipt Footer</label>
              <input value={draft.receiptFooter ?? ''} onChange={(e) => set({ receiptFooter: e.target.value })} />
            </div>
            <div className="field">
              <label>Logo URL</label>
              <input value={draft.logoUrl ?? ''} onChange={(e) => set({ logoUrl: e.target.value })} />
            </div>
            <div className="field-row">
              <div className="field">
                <label>Allow Cashier Discounts</label>
                <select value={draft.allowCashierDiscount ? 'true' : 'false'}
                  onChange={(e) => set({ allowCashierDiscount: e.target.value === 'true' })}>
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              </div>
              <div className="field">
                <label>Max Discount (%)</label>
                <input type="number" step="0.01" value={draft.maxDiscountPercent}
                  onChange={(e) => set({ maxDiscountPercent: Number(e.target.value) })} />
              </div>
              <div className="field">
                <label>Require Admin to Cancel</label>
                <select value={draft.requireAdminCancel ? 'true' : 'false'}
                  onChange={(e) => set({ requireAdminCancel: e.target.value === 'true' })}>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>

            {error && <div className="form-error">{error}</div>}
            <div className="row gap-sm" style={{ marginTop: 12 }}>
              <button className="btn btn-primary" onClick={save} disabled={m.isPending}>
                {m.isPending ? 'Saving…' : 'Save Settings'}
              </button>
              {saved && <span className="badge badge-green">Saved ✓</span>}
            </div>
          </Card>
        </>
      )}
    </>
  );
}
